import { Trans, useLingui } from '@lingui/react/macro'
import { Badge } from 'designSystem/components/Badge'
import { Button } from 'designSystem/components/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from 'designSystem/components/DropdownMenu'
import { Switch } from 'designSystem/components/Switch'
import { Text } from 'designSystem/components/Text'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'designSystem/components/Tooltip'
import { DocChatBetaSwitch } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/search/DocChatBetaSwitch'
import { useAdvancedSearchUX } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/AdvancedSearchUXContext'
import { useDocChatCapabilities } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocChatCapabilities'
import { useDocChatCmmsSetting } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocChatCmmsSetting'
import { useDocChatMultiCallSetting } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocChatMultiCallSetting'
import { useDocChatTimeSeriesSetting } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocChatTimeSeriesSetting'
import { hasDocChatCapability } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatCapabilities'
import { PermissionsGate } from 'features/permissions'
import {
  ChartLine,
  ChevronDown,
  Info,
  type LucideIcon,
  Workflow,
  Wrench,
} from 'lucide-react'
import React from 'react'
import styled from 'styled-components'
import { ROLE_CANTILEVER_DOCCHAT_BETA_VIEWER } from 'types'

const AgentDivider = styled.span`
  width: 0.0625rem;
  height: 0.875rem;
  margin-inline: 0.25rem;
  flex-shrink: 0;
  background: var(--border);
`

const BetaChip = styled(Badge)`
  margin-inline-start: auto;
`

const AgentName = styled.span`
  display: inline-flex;
  flex: 1;
  align-items: center;
  gap: 0.5rem;
`

interface AgentRowProps {
  label: string
  icon: LucideIcon
  description: string
  enabled: boolean
  beta?: boolean
  onToggle: (enabled: boolean) => void
}

interface AgentOption extends AgentRowProps {
  id: string
  available: boolean
}

function stopInfoClick(event: React.MouseEvent<HTMLButtonElement>) {
  event.stopPropagation()
}

function stopInfoActivation(event: React.KeyboardEvent<HTMLButtonElement>) {
  if (event.key === 'Enter' || event.key === ' ') event.stopPropagation()
}

function AgentRow({
  label,
  icon: Icon,
  description,
  enabled,
  beta = false,
  onToggle,
}: AgentRowProps): React.ReactElement {
  const { t } = useLingui()

  return (
    <DropdownMenuItem
      variant='comfortable'
      role='menuitemcheckbox'
      aria-checked={enabled}
      aria-description={description}
      onSelect={(event) => {
        event.preventDefault()
        onToggle(!enabled)
      }}>
      <Icon aria-hidden />
      <AgentName>
        <Text variant='body' wrap='nowrap'>
          {label}
        </Text>
        <Tooltip disableHoverableContent>
          <TooltipTrigger asChild>
            <Button
              variant='ghostMuted'
              size='icon-xs'
              type='button'
              tabIndex={-1}
              aria-label={t`Tool information`}
              onClick={stopInfoClick}
              onKeyDown={stopInfoActivation}>
              <Info aria-hidden />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='top' width='measure'>
            {description}
          </TooltipContent>
        </Tooltip>
        {beta ? (
          <BetaChip variant='outlineMuted'>
            <Text variant='overline' color='muted'>
              <Trans>Beta</Trans>
            </Text>
          </BetaChip>
        ) : null}
      </AgentName>
      <Switch size='sm' checked={enabled} tabIndex={-1} aria-hidden />
    </DropdownMenuItem>
  )
}

function preventTooltipOnFocus(event: React.FocusEvent<HTMLButtonElement>) {
  event.preventDefault()
}

export function DocChatAgentsMenu(): React.ReactElement | null {
  const { siteId } = useAdvancedSearchUX()
  const { data: capabilities } = useDocChatCapabilities(siteId)
  const { enabled: timeSeriesEnabled, setEnabled: setTimeSeriesEnabled } =
    useDocChatTimeSeriesSetting()
  const { enabled: cmmsEnabled, setEnabled: setCmmsEnabled } =
    useDocChatCmmsSetting()
  const { enabled: multiCallEnabled, setEnabled: setMultiCallEnabled } =
    useDocChatMultiCallSetting()
  const { t } = useLingui()

  const agents: AgentOption[] = [
    {
      id: 'multi_call',
      available: capabilities?.multi_call === true,
      enabled: multiCallEnabled,
      icon: Workflow,
      label: t`Multicall`,
      description: t`Searches documents in multiple steps to answer your next question`,
      beta: true,
      onToggle: setMultiCallEnabled,
    },
    {
      id: 'time_series',
      available: hasDocChatCapability(capabilities, 'time_series'),
      enabled: timeSeriesEnabled,
      icon: ChartLine,
      label: t`Time series`,
      description: t`Reads and charts plant data on your next question`,
      beta: true,
      onToggle: setTimeSeriesEnabled,
    },
    {
      id: 'cmms',
      available: hasDocChatCapability(capabilities, 'cmms'),
      enabled: cmmsEnabled,
      icon: Wrench,
      label: t`CMMS`,
      description: t`Includes imported maintenance work orders in your search results.`,
      beta: true,
      onToggle: setCmmsEnabled,
    },
  ]
  const availableAgents = agents.filter((agent) => agent.available)
  const activeAgents = availableAgents.filter((agent) => agent.enabled)
  const activeAgentNames =
    activeAgents.map((agent) => agent.label).join(', ') || t`None`
  const activeAgentsDescription = t`Tools on: ${activeAgentNames}`

  if (availableAgents.length === 0) return null

  function setAllAgentsEnabled(enabled: boolean) {
    agents.forEach((agent) => agent.onToggle(enabled))
  }

  const toolsMenu = (
    <DropdownMenu>
      <Tooltip delayDuration={1000} disableHoverableContent>
        <TooltipTrigger asChild onFocus={preventTooltipOnFocus}>
          <DropdownMenuTrigger asChild>
            <Button
              variant='outline'
              shape='round'
              size='default'
              type='button'
              aria-description={activeAgentsDescription}>
              <Trans>Tools</Trans>
              {activeAgents.length > 0 ? <AgentDivider aria-hidden /> : null}
              {activeAgents.map(({ id, icon: Icon }) => (
                <Icon key={id} aria-hidden />
              ))}
              <ChevronDown aria-hidden data-icon='inline-end' />
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side='top'>{activeAgentsDescription}</TooltipContent>
      </Tooltip>
      <DropdownMenuContent align='start' side='bottom' width='auto'>
        <DropdownMenuLabel>
          <Text variant='caption' color='muted'>
            <Trans>Tools</Trans>
          </Text>
        </DropdownMenuLabel>
        {availableAgents.map((agent) => (
          <AgentRow key={agent.id} {...agent} />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )

  return (
    <PermissionsGate
      requires={[ROLE_CANTILEVER_DOCCHAT_BETA_VIEWER]}
      fallback={toolsMenu}>
      <DocChatBetaSwitch
        enabled={activeAgents.length > 0}
        onToggle={setAllAgentsEnabled}
      />
    </PermissionsGate>
  )
}
